import type { DisplayState } from '@features/patient-requirements';
import { requestedDetail, resolveDisplayState } from '@features/patient-requirements';

/**
 * The `patient_requirement` block each row gains when the patients list is
 * filtered by a requirement (GDN-3423). Hand-written from the shape in the
 * backend PR; the generated type replaces it when the contract re-syncs, and
 * this file is the only place that changes.
 *
 * Note what is NOT here: `display_state`. The block carries the storage `state`
 * only, which is what `resolveDisplayState` prefers anyway.
 */
export interface PatientRequirementBlockDto {
  patient_requirement_id?: string | null;
  state: string;
  has_active_session?: boolean | null;
  last_requested_at?: string | null;
  last_request_channel?: string | null;
}

export interface RequirementColumnView {
  state: DisplayState;
  /** The "Requested on Aug 30" line, when there is one. */
  detail?: string;
}

/**
 * One patient's requirement, as the filtered list renders it.
 *
 * Deliberately routed through the SAME two helpers the record section and the
 * missing list use. Three surfaces now show one requirement, and the only way
 * they cannot disagree is by sharing the derivation rather than each writing
 * their own.
 *
 * In particular the Requested state comes from `has_active_session` and never
 * from the presence of `last_requested_at`. After GDN-3363 a row can carry a
 * timestamp and still not be requested, because a bounce demotes it — so a
 * timestamp means "we tried", not "they were asked and we are waiting".
 */
export const toRequirementColumn = (
  block: PatientRequirementBlockDto,
): RequirementColumnView => {
  const state = resolveDisplayState(
    { state: block.state },
    block.has_active_session ?? false,
  );

  return {
    state,
    detail:
      state === 'requested'
        ? requestedDetail(block.last_requested_at)
        : undefined,
  };
};

/** Columns as fetched, together with the definition they describe. */
export interface ColumnsByFilter {
  definitionId: string | null;
  columns: Record<string, RequirementColumnView>;
}

const NO_COLUMNS: Record<string, RequirementColumnView> = {};

/**
 * The columns to render right now — which is none unless they describe the
 * filter that is currently active.
 *
 * Two failures this closes, both of which put one requirement's status under
 * another requirement's chip:
 *
 * - **Between filters.** Changing or clearing the filter leaves the previous
 *   response on screen until the new one lands. Without this check the old
 *   columns stay visible against the new chip for the length of a request.
 * - **A late response.** A slow fetch for the OLD filter can land after the
 *   new one, and would otherwise repaint the columns it carries.
 *
 * An empty column is honest. A stale one is not, and it is worse than empty
 * because it reads as an answer.
 */
export const columnsForActiveFilter = (
  activeDefinitionId: string | null | undefined,
  produced: ColumnsByFilter,
): Record<string, RequirementColumnView> =>
  activeDefinitionId && activeDefinitionId === produced.definitionId
    ? produced.columns
    : NO_COLUMNS;
